'use client';

import React from 'react';
import { useProductService, useRegionCurrency } from '@skin-seoul/service';
import { ProductStockStatus } from '@skin-seoul/service';
import { HStack, Text } from '@skin-seoul/design-system';
import RecentlyViewedListSkeleton from '@/containers/home/sections/RecentlyViewedSection/RecentlyViewedListSkeleton';
import { QueryBoundary } from '@/components/ui/QueryBoundary';
import { RecentlyViewedList } from '@/containers/home/sections/RecentlyViewedSection/RecentlyViewedList';
import { LucideIcon } from '@skin-seoul/shared-components';
import { useSafeRouter } from '@/hooks';
import { useParams } from 'next/navigation';
import { escape } from 'es-toolkit/compat';
import { shuffle } from 'es-toolkit';
import SectionContainer from '../../components/SectionContainer';
import type { Category } from '@skin-seoul/service';
const ProductSameCategorySection = () => {
  const params = useParams();
  const router = useSafeRouter();
  const {
    currency
  } = useRegionCurrency();
  const {
    useGetSuspenseProductsByIds: useGetProductsByIds,
    useGetProductsCollections,
    useGetProductsDetail
  } = useProductService();
  const {
    data: {
      categories
    }
  } = useGetProductsDetail({
    slug: params.slug as string,
    currency
  });
  const {
    data: {
      products: categoryProducts
    }
  } = useGetProductsCollections({
    collectionName: escape(categories[0].name),
    limit: 30,
    currency,
    stockStatus: ProductStockStatus.instock
  });
  const categoryProductIds = categoryProducts?.map(product => product.productId);
  const handleViewAll = () => {
    if (categories && categories.length > 0) {
      const category = categories[0];
      const categoryMap = new Map<number, Category>();
      const allCategories: Category[] = [];
      categories.forEach(cat => {
        categoryMap.set(cat.termId, cat);
        allCategories.push(cat);
        if (cat.categoryHierarchy && cat.categoryHierarchy.length > 0) {
          cat.categoryHierarchy.forEach(hierarchy => {
            const subCategory: Category = {
              termId: hierarchy.termId,
              name: hierarchy.name,
              slug: hierarchy.slug,
              taxonomy: hierarchy.taxonomy,
              description: '',
              count: 0,
              parentTermId: cat.termId,
              categoryHierarchy: hierarchy.categoryHierarchy || [],
              rankInTerm: 0
            };
            categoryMap.set(hierarchy.termId, subCategory);
            allCategories.push(subCategory);
          });
        }
      });
      const categoryChain: Category[] = [];
      let currentCategory: Category | null = category;
      const visitedIds = new Set<number>();
      while (currentCategory && !visitedIds.has(currentCategory.termId)) {
        visitedIds.add(currentCategory.termId);
        categoryChain.unshift(currentCategory);
        if (currentCategory.parentTermId && currentCategory.parentTermId !== 0) {
          currentCategory = categoryMap.get(currentCategory.parentTermId) || null;
        } else {
          currentCategory = null;
        }
      }
      let currentPath = '';
      categoryChain.forEach(cat => {
        currentPath = currentPath ? `${currentPath}/${cat.slug}` : cat.slug;
      });
      router.push(`/collections/${currentPath}`);
    } else {}
  };
  const {
    data: categoryProductsData
  } = useGetProductsByIds({
    ids: categoryProductIds.join(','),
    currency
  });
  const filteredCategoryProducts = categoryProductsData ? shuffle(categoryProductsData.filter(product => product.status === ProductStockStatus.instock)).slice(0, 10) : undefined;
  if (!filteredCategoryProducts || filteredCategoryProducts.length === 0) {
    return null;
  }
  return <SectionContainer headerTitle="Same Category" headerRightContent={<HStack gap={1} alignItems="center" justifyContent="flex-end" cursor="pointer" onClick={handleViewAll}>
          <Text textStyle={{
      base: 'label.small',
      tablet: 'label.medium'
    }} fontWeight="normal" color="neutral.600" textAlign="left" whiteSpace="nowrap">
            View all
          </Text>
          <LucideIcon name="ChevronRight" size={{
      base: 'md',
      tablet: 'lg'
    }} color="neutral.600" />
        </HStack>} data-sentry-element="SectionContainer" data-sentry-component="ProductSameCategorySection" data-sentry-source-file="ProductSameCategorySection.tsx">
      <QueryBoundary clientOnly loadingFallback={<RecentlyViewedListSkeleton length={4} />} data-sentry-element="QueryBoundary" data-sentry-source-file="ProductSameCategorySection.tsx">
        <RecentlyViewedList recentlyViewedProducts={filteredCategoryProducts} data-sentry-element="RecentlyViewedList" data-sentry-source-file="ProductSameCategorySection.tsx" />
      </QueryBoundary>
    </SectionContainer>;
};
export default ProductSameCategorySection;